import { useModal } from "@hooks/useModal";
import { useToast } from "@hooks/useToast";
import { useTracking } from "@hooks/useTracking";
import { EmployeeListItemFragment } from "@src/fragments.generated";
import { launchConfetti } from "@utils/confetti";
import { css, StyleSheet } from "aphrodite";
import gql from "graphql-tag";
import { FC } from "react";
import { HrisEmployeeFragment } from "../settings/hris/HrisEmployeeListContainer.generated";
import { useCreateHrisEmployeeLinkMutation } from "./ConfirmLinkHrisEmployeeModal.generated";
import LinkHrisEmployeeModal from "./LinkHrisEmployeeModal";
import Modal from "./Modal";
import { GraphqlOperations } from "@src/types.generated";

type Props = {
  hrisEmployee: HrisEmployeeFragment;
  opusEmployee: EmployeeListItemFragment;
};

const ConfirmLinkHrisEmployeeModal: FC<Props> = ({
  hrisEmployee,
  opusEmployee,
}) => {
  const { showModal, closeModal } = useModal();
  const { addErrorToast } = useToast();
  const { trackEvent } = useTracking();
  const [createHrisEmployeeLink] = useCreateHrisEmployeeLinkMutation({
    variables: {
      hrisEmployeeId: hrisEmployee.id,
      traineeId: opusEmployee.id,
    },
    refetchQueries: [GraphqlOperations.Query.HrisEmployees],
    onCompleted: (data) => {
      if (!data.createHrisEmployeeLink.success) {
        addErrorToast({
          hrisEmployeeId: hrisEmployee.id,
          opusEmployeeId: opusEmployee.id,
          callsite: "new_hris_employee_link",
        });
      } else {
        trackEvent("new_hris_employee_link", {
          hrisEmployeeId: hrisEmployee.id,
          opusEmployeeId: opusEmployee.id,
        });
        closeModal();
        launchConfetti();
      }
    },
    onError: (error) => {
      addErrorToast({
        ...error,
        callsite: "new_group_modal",
      });
    },
  });

  const hrisEmployeeName = hrisEmployee.firstName + " " + hrisEmployee.lastName;

  return (
    <Modal
      className={css(styles.modal)}
      title={`Link ${hrisEmployeeName}`}
      cancelButtonProps={{
        copy: "Cancel",
        onClick: () => {
          showModal(<LinkHrisEmployeeModal hrisEmployee={hrisEmployee} />);
        },
      }}
      confirmButtonProps={{
        copy: "Confirm",
        onClick: () => {
          createHrisEmployeeLink();
        },
      }}
    >
      <div className={css(styles.body)}>
        Are you sure you want to connect the HRIS user for{" "}
        <b>{hrisEmployeeName}</b> to the Opus user for{" "}
        <b>{opusEmployee.name}</b>?
        {opusEmployee.hrisEmployee?.id != null && (
          <div style={{ marginTop: 12 }}>
            Warning: this will override the Opus user's existing link to the
            HRIS employee{" "}
            <b>
              {opusEmployee.hrisEmployee.firstName}{" "}
              {opusEmployee.hrisEmployee.lastName}
            </b>
            .
          </div>
        )}
      </div>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modal: {
    minWidth: 500,
  },
  body: {
    padding: 20,
  },
});

gql`
  mutation CreateHrisEmployeeLink($traineeId: Int!, $hrisEmployeeId: Int!) {
    createHrisEmployeeLink(
      traineeId: $traineeId
      hrisEmployeeId: $hrisEmployeeId
    ) {
      success
    }
  }
`;

export default ConfirmLinkHrisEmployeeModal;
